/**
 * Bridges Firebase Remote Config into the TanStack Query cache.
 *
 * Remote Config is a single global instance — one fetch, one realtime stream,
 * one activated snapshot every parameter reads from — so the whole app shares
 * a single cache entry holding the full snapshot, and consumers pick their
 * parameter out of it via `select`. The query function resolves with the
 * activated snapshot and installs the one `onConfigUpdate` listener; later
 * updates are written straight into the cache.
 */
import type { QueryClient, QueryFunction } from "@tanstack/react-query";
import {
  activate,
  ensureInitialized,
  fetchAndActivate,
  getAll,
  isSupported,
  onConfigUpdate,
  type RemoteConfig,
  type Unsubscribe,
} from "firebase/remote-config";
import { getRemoteConfig } from "utilities/firebase";
import { attempt } from "@/utilities/attempt";
import { REMOTE_CONFIG_QUERY_KEY, type RemoteConfigQueryKey } from "./queryKey";
import type { RemoteConfigFailure, RemoteConfigSnapshot } from "./types";

export const REFETCH_AFTER_FAILED_FETCH_MS = 60_000;

const EMPTY_SNAPSHOT: RemoteConfigSnapshot = {};

let installed = false;
let unsubscribe: Unsubscribe | undefined;
let refetchTimer: ReturnType<typeof setTimeout> | undefined;
let snapshotFailure: RemoteConfigFailure | undefined;

export const getSnapshotFailure = () => snapshotFailure;

const fail = (
  reason: RemoteConfigFailure["reason"],
  error?: unknown,
): RemoteConfigSnapshot => {
  snapshotFailure = { reason, error };
  return EMPTY_SNAPSHOT;
};

// Should only re-render the keys that actually changed
const reuseUnchanged = (
  previous: RemoteConfigSnapshot | undefined,
  next: RemoteConfigSnapshot,
): RemoteConfigSnapshot => {
  if (!previous) return next;

  const previousKeys = Object.keys(previous);
  const entries = Object.entries(next);
  let changed = previousKeys.length !== entries.length;
  const merged: RemoteConfigSnapshot = {};

  for (const [key, value] of entries) {
    const before = previous[key];
    if (before && before.asString() === value.asString()) {
      merged[key] = before;
    } else {
      merged[key] = value;
      changed = true;
    }
  }

  return changed ? merged : previous;
};

const installListener = (client: QueryClient, remoteConfig: RemoteConfig) => {
  if (installed) return;
  installed = true;

  const registered = attempt(() =>
    onConfigUpdate(remoteConfig, {
      next: async () => {
        await activate(remoteConfig);
        client.setQueryData<RemoteConfigSnapshot>(
          REMOTE_CONFIG_QUERY_KEY,
          (previous) => reuseUnchanged(previous, getAll(remoteConfig)),
        );
      },
      error: (error) => {
        console.error("Remote config update error:", error);
      },
      complete: () => {},
    }),
  );

  if (registered.ok) {
    unsubscribe = registered.data;
  } else {
    installed = false;
    console.error(
      "Failed to listen for remote config updates:",
      registered.error,
    );
  }
};

const scheduleRefetch = (client: QueryClient) => {
  if (refetchTimer) return;

  refetchTimer = setTimeout(() => {
    refetchTimer = undefined;
    void client.invalidateQueries({ queryKey: REMOTE_CONFIG_QUERY_KEY });
  }, REFETCH_AFTER_FAILED_FETCH_MS);
};

export const remoteConfigQueryFn: QueryFunction<
  RemoteConfigSnapshot,
  RemoteConfigQueryKey
> = async ({ client }) => {
  const supported = await attempt(() => isSupported());
  if (!supported.ok) return fail("init_failed", supported.error);
  if (!supported.data) return fail("unsupported");

  const config = attempt(() => getRemoteConfig());
  if (!config.ok) return fail("init_failed", config.error);
  if (!config.data) return fail("unavailable");
  const remoteConfig = config.data;

  await attempt(() => ensureInitialized(remoteConfig));

  const fetched = await attempt(() => fetchAndActivate(remoteConfig));
  if (!fetched.ok) {
    console.error("Failed to fetch remote config:", fetched.error);
    scheduleRefetch(client);
  }

  installListener(client, remoteConfig);

  snapshotFailure = fetched.ok
    ? undefined
    : { reason: "fetch_failed", error: fetched.error };

  return getAll(remoteConfig);
};

// for tests
export const teardownRemoteConfig = () => {
  unsubscribe?.();
  unsubscribe = undefined;
  installed = false;
  snapshotFailure = undefined;
  if (refetchTimer) {
    clearTimeout(refetchTimer);
    refetchTimer = undefined;
  }
};
